import React, { useState, useEffect } from "react";
import axios from "axios";
import { useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";
import { Edit2, Trash, Save, X, LogIn, Send, User } from "lucide-react";
import { SERVER_URL } from "../../constants";

const CommentSection = ({ productId }) => {
  const { user, isLoaded } = useUser();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedComment, setEditedComment] = useState("");
  const [name, setName] = useState("");

  // Fetch user name if logged in
  useEffect(() => {
    async function getName() {
      if (!user) return;
      try {
        const res = await axios.get(`${SERVER_URL}/api/v1/user/${user.id}`);
        const { data } = res.data;
        setName(data.firstName);
      } catch (error) {
        console.error("Error fetching user name:", error);
      }
    }
    getName();
  }, [user]);

  // Fetch comments on load
  useEffect(() => {
    const fetchComments = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${SERVER_URL}/api/v1/comments/${productId}`);
        setComments(response.data.data || []);
      } catch (error) {
        console.error("Error fetching comments:", error);
        toast.error("Failed to load comments.");
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [productId]);

  // Check if user is logged in before actions
  const checkAuth = () => {
    if (!user) {
      toast.error("Please log in to interact with comments.");
      return false;
    }
    return true;
  };

  // Add new comment
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!checkAuth()) return;

    if (!newComment.trim()) {
      toast.error("Comment cannot be empty.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${SERVER_URL}/api/v1/comments/add`, {
        userId: user.id,
        productId,
        comment: newComment.trim(),
        username: name || "Anonymous",
        createdAt: new Date().toISOString(),
      });

      setComments((prev) => [response.data.data, ...prev]);
      setNewComment("");
      toast.success("Comment added successfully!");
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment.");
    } finally {
      setSubmitting(false);
    }
  };

  // Edit a comment
  const handleEditComment = async (commentId) => {
    if (!checkAuth()) return;

    if (!editedComment.trim()) {
      toast.error("Comment cannot be empty.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.put(`${SERVER_URL}/api/v1/comments/update/${commentId}`, {
        userId: user.id,
        comment: editedComment.trim(),
      });

      setComments((prev) =>
        prev.map((comment) =>
          comment._id === commentId ? { ...comment, comment: response.data.data.comment } : comment
        )
      );

      setEditingCommentId(null);
      setEditedComment("");
      toast.success("Comment updated successfully!");
    } catch (error) {
      console.error("Error updating comment:", error);
      toast.error(error.response?.data?.message || "Failed to update comment.");
    } finally {
      setLoading(false);
    }
  };

  // Delete a comment
  const handleDeleteComment = async (commentId) => {
    if (!checkAuth()) return;

    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return;
    }

    setLoading(true);

    try {
      await axios.delete(`${SERVER_URL}/api/v1/comments/delete/${commentId}`, {
        data: { userId: user.id },
      });

      setComments((prev) => prev.filter((comment) => comment._id !== commentId));
      toast.success("Comment deleted successfully!");
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error(error.response?.data?.message || "Failed to delete comment.");
    } finally {
      setLoading(false);
    }
  };

  // Handle navigation to login
  const handleNavigateToLogin = () => {
    // Remove console.log and make sure the navigation works
    navigate('/login');
  };

  // Render comment input section based on auth status
  const renderCommentInput = () => {
    if (!isLoaded) return null;

    if (!user) {
      return (
        <div className="relative bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 sm:p-8 text-center text-white mb-8">
          <div className="absolute inset-0 bg-opacity-10 bg-black rounded-xl animate-pulse"></div>
          <h3 className="text-xl sm:text-2xl font-semibold mb-3">Join the Conversation!</h3>
          <p className="text-sm sm:text-base mb-4">
            Share your thoughts about this product. Sign in to leave a comment.
          </p>
          <button
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-full font-semibold text-sm sm:text-base shadow-lg hover:bg-blue-50 transition-all"
            onClick={handleNavigateToLogin}
            type="button" // Add type="button" to prevent any form submission
          >
            <LogIn size={20} />
            Sign In to Comment
          </button>
        </div>
      );
    }

    return (
      <form onSubmit={handleAddComment} className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gray-100 rounded-full p-2">
            <User size={24} className="text-gray-700" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{name || "Anonymous"}</p>
          </div>
        </div>
        <div className="flex gap-4 items-start">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write your comment..."
            className="w-full h-24 sm:h-32 p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm sm:text-base"
            rows="3"
          />
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-semibold text-sm sm:text-base hover:bg-blue-700 transition-all disabled:opacity-50"
          >
            {submitting ? (
              <ClipLoader size={20} color="#fff" />
            ) : (
              <>
                <Send size={20} />
                Post
              </>
            )}
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className="container mx-auto px-4 max-w-7xl py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">
        Customer Comments
      </h2>

      {/* Comment Input Section */}
      {renderCommentInput()}

      {/* Comments List */}
      {loading && comments.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <ClipLoader size={50} color="#2563eb" />
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          No comments yet. Be the first to share your thoughts!
        </div>
      ) : (
        <div className="space-y-6">
          {comments.map((comment) => (
            <div
              key={comment._id}
              className="bg-white rounded-xl shadow-md p-4 sm:p-6 border-l-4 border-blue-500"
            >
              {editingCommentId === comment._id ? (
                <div className="flex gap-4 mb-4">
                  <textarea
                    value={editedComment}
                    onChange={(e) => setEditedComment(e.target.value)}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-200 text-sm sm:text-base"
                    rows="3"
                  />
                  <button
                    onClick={() => handleEditComment(comment._id)}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400"
                  >
                    <Save size={16} />
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingCommentId(null);
                      setEditedComment("");
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    <X size={16} />
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="bg-gray-100 rounded-full p-2">
                    <User size={24} className="text-gray-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <p className="font-semibold text-gray-900">{comment.username || name || "Anonymous"}</p>
                        <span className="text-xs text-gray-500">
                          {new Date(comment.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      {user?.id === comment.userId && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingCommentId(comment._id);
                              setEditedComment(comment.comment);
                            }}
                            className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                          >
                            <Edit2 size={16} /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteComment(comment._id)}
                            className="text-red-600 hover:underline flex items-center gap-1 text-sm"
                          >
                            <Trash size={16} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-gray-700 text-sm sm:text-base">{comment.comment}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentSection;
